import { deleteObject, ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import { TenantPublishingError } from './types';

const MAX_SITE_ASSET_BYTES = 5 * 1024 * 1024;
const ALLOWED_SITE_ASSET_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export const HERO_VIDEO_LIMITS = {
  maxBytes: 8 * 1024 * 1024,
  maxSeconds: 15,
  maxLongEdge: 1920,
  maxShortEdge: 1080,
  recommended: '1280×720',
} as const;

export const HERO_VIDEO_REQUIREMENTS =
  `MP4 (H.264), up to 8 MB and ${HERO_VIDEO_LIMITS.maxSeconds} seconds, at most 1920×1080. ` +
  `${HERO_VIDEO_LIMITS.recommended} is recommended.`;

export type SiteAssetUploadProgress = (percentage: number) => void;

const safeAssetId = (): string => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return random.replace(/[^A-Za-z0-9_-]/g, '-');
};

const assertTenantId = (tenantId: string) => {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(tenantId)) {
    throw new TenantPublishingError('INVALID_ARGUMENT', 'The active tenant is invalid.');
  }
};

const uploadToSiteAssets = async (
  tenantId: string,
  file: File,
  onProgress: SiteAssetUploadProgress | undefined,
  failureMessage: string
): Promise<string> => {
  const path = `booking-websites/${tenantId}/site-assets/${safeAssetId()}`;
  const objectReference = ref(storage, path);
  const task = uploadBytesResumable(objectReference, file, {
    contentType: file.type,
    cacheControl: 'public,max-age=31536000,immutable',
  });
  try {
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', (snapshot) => {
        onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      }, reject, resolve);
    });
    return path;
  } catch (error) {
    try {
      await deleteObject(objectReference);
    } catch {
      // A failed resumable upload commonly leaves no object to remove.
    }
    throw new TenantPublishingError(
      'UNKNOWN',
      error instanceof Error ? error.message : failureMessage
    );
  }
};

export const uploadTenantSiteAsset = async (
  tenantId: string,
  file: File,
  onProgress?: SiteAssetUploadProgress
): Promise<string> => {
  assertTenantId(tenantId);
  if (!ALLOWED_SITE_ASSET_TYPES.has(file.type)) {
    throw new TenantPublishingError('INVALID_ARGUMENT', 'Use a JPEG, PNG, WebP, or GIF image.');
  }
  if (file.size <= 0 || file.size > MAX_SITE_ASSET_BYTES) {
    throw new TenantPublishingError('INVALID_ARGUMENT', 'Site images must be smaller than 5 MB.');
  }
  return uploadToSiteAssets(tenantId, file, onProgress, 'The image upload failed.');
};

// MP4 boxes that can contain the sample descriptions, outermost first.
const MP4_CONTAINER_BOXES = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl']);
const H264_SAMPLE_ENTRIES = new Set(['avc1', 'avc3']);
const VIDEO_SAMPLE_ENTRIES = new Set(['avc1', 'avc3', 'hvc1', 'hev1', 'av01', 'vp08', 'vp09', 'mp4v', 'dvh1', 'dvhe']);

const fourCc = (view: DataView, offset: number): string =>
  String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));

/**
 * The video codecs an MP4 declares, read from its sample descriptions
 * (moov/trak/mdia/minf/stbl/stsd) rather than guessed from the file name.
 */
export const mp4VideoCodecs = (buffer: ArrayBuffer): string[] => {
  const view = new DataView(buffer);
  const codecs: string[] = [];
  const walk = (start: number, end: number, depth: number) => {
    let offset = start;
    while (offset + 8 <= end && depth < 8) {
      let size = view.getUint32(offset);
      const type = fourCc(view, offset + 4);
      let header = 8;
      if (size === 1) {
        if (offset + 16 > end) return;
        const high = view.getUint32(offset + 8);
        const low = view.getUint32(offset + 12);
        size = high * 2 ** 32 + low;
        header = 16;
      } else if (size === 0) {
        size = end - offset;
      }
      if (size < header || offset + size > end) return;
      if (MP4_CONTAINER_BOXES.has(type)) {
        walk(offset + header, offset + size, depth + 1);
      } else if (type === 'stsd' && offset + header + 8 <= offset + size) {
        // Full box: version/flags, then the entry count, then one box per entry.
        let entry = offset + header + 8;
        while (entry + 8 <= offset + size) {
          const entrySize = view.getUint32(entry);
          const entryType = fourCc(view, entry + 4);
          if (VIDEO_SAMPLE_ENTRIES.has(entryType)) codecs.push(entryType);
          if (entrySize < 8) break;
          entry += entrySize;
        }
      }
      offset += size;
    }
  };
  walk(0, buffer.byteLength, 0);
  return codecs;
};

export type HeroVideoMetadata = { duration: number; width: number; height: number };

const readFileBuffer = (file: File): Promise<ArrayBuffer> =>
  typeof file.arrayBuffer === 'function'
    ? file.arrayBuffer()
    : new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });

/** Reads duration and size from the browser without playing or uploading the file. */
export const readVideoMetadata = (file: File): Promise<HeroVideoMetadata> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const finish = (callback: () => void) => {
    window.clearTimeout(timer);
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
    callback();
  };
  const timer = window.setTimeout(
    () => finish(() => reject(new Error('timeout'))),
    10_000
  );
  video.preload = 'metadata';
  video.muted = true;
  video.onloadedmetadata = () => {
    // Read before finish() resets the element, which clears these values.
    const metadata = { duration: video.duration, width: video.videoWidth, height: video.videoHeight };
    finish(() => resolve(metadata));
  };
  video.onerror = () => finish(() => reject(new Error('unreadable')));
  video.src = url;
});

export type HeroVideoInspector = {
  readBuffer: (file: File) => Promise<ArrayBuffer>;
  readMetadata: (file: File) => Promise<HeroVideoMetadata>;
};

const browserInspector: HeroVideoInspector = { readBuffer: readFileBuffer, readMetadata: readVideoMetadata };

const invalid = (message: string) => new TenantPublishingError('INVALID_ARGUMENT', message);

/**
 * Checks everything Storage rules cannot: codec, length and resolution.
 * There is no transcoding, so a file that fails here never reaches Storage.
 */
export const validateHeroVideoFile = async (
  file: File,
  inspector: HeroVideoInspector = browserInspector
): Promise<HeroVideoMetadata> => {
  if (file.type !== 'video/mp4') {
    throw invalid('Use an MP4 video. Other formats, such as MOV or WebM, are not supported.');
  }
  if (file.size <= 0 || file.size > HERO_VIDEO_LIMITS.maxBytes) {
    throw invalid(`Background videos must be 8 MB or smaller. This file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
  }
  let codecs: string[];
  try {
    codecs = mp4VideoCodecs(await inspector.readBuffer(file));
  } catch {
    throw invalid('This file could not be read as an MP4 video.');
  }
  if (!codecs.length) {
    throw invalid('This file does not contain a video track that browsers can play.');
  }
  if (!codecs.every((codec) => H264_SAMPLE_ENTRIES.has(codec))) {
    throw invalid(
      'This video is not H.264. Re-export it as MP4 with H.264 (for example "Most Compatible" on iPhone) and try again.'
    );
  }
  let metadata: HeroVideoMetadata;
  try {
    metadata = await inspector.readMetadata(file);
  } catch {
    throw invalid('This browser could not read the video. Re-export it as MP4 with H.264 and try again.');
  }
  if (!Number.isFinite(metadata.duration) || metadata.duration <= 0) {
    throw invalid('The video length could not be read.');
  }
  if (metadata.duration > HERO_VIDEO_LIMITS.maxSeconds + 0.05) {
    throw invalid(`Background videos can be at most ${HERO_VIDEO_LIMITS.maxSeconds} seconds. This one is ${Math.round(metadata.duration)} seconds.`);
  }
  const longEdge = Math.max(metadata.width, metadata.height);
  const shortEdge = Math.min(metadata.width, metadata.height);
  if (!longEdge || longEdge > HERO_VIDEO_LIMITS.maxLongEdge || shortEdge > HERO_VIDEO_LIMITS.maxShortEdge) {
    throw invalid(
      `Background videos can be at most 1920×1080. This one is ${metadata.width}×${metadata.height}; ` +
      `${HERO_VIDEO_LIMITS.recommended} is recommended.`
    );
  }
  return metadata;
};

export type HeroVideoUploadStage = 'checking' | 'uploading';

export const uploadTenantSiteVideo = async (
  tenantId: string,
  file: File,
  onProgress?: SiteAssetUploadProgress,
  onStage?: (stage: HeroVideoUploadStage) => void,
  inspector?: HeroVideoInspector
): Promise<string> => {
  assertTenantId(tenantId);
  onStage?.('checking');
  await validateHeroVideoFile(file, inspector);
  onStage?.('uploading');
  return uploadToSiteAssets(tenantId, file, onProgress, 'The video upload failed.');
};
